/**
 * WebMCP: expose a few safe, read-only site tools to AI agents running in the
 * browser via the experimental `navigator.modelContext` API.
 *
 * https://webmachinelearning.github.io/webmcp/
 *
 * The API is experimental and only present in some browsers / behind flags, so
 * everything here is fully guarded: if the API is missing we no-op. Tools are
 * information/navigation only: nothing destructive or state-changing.
 */

interface WebMcpTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (
    args: Record<string, unknown>
  ) => Promise<{ content: Array<{ type: string; text: string }> }>;
}

interface ModelContext {
  provideContext: (ctx: { tools: WebMcpTool[] }) => void;
}

const SITE = 'https://7sigma.io';

function text(t: string) {
  return { content: [{ type: 'text', text: t }] };
}

const tools: WebMcpTool[] = [
  {
    name: 'get_company_info',
    description: 'Get an overview of 7Sigma: what the company does and its positioning.',
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    execute: async () =>
      text(
        '7Sigma is a senior technical leadership practice in Orlando, Florida, serving clients nationwide. ' +
          'Principal: Rob Christian. ' +
          '7Sigma picks up where a team leaves off (rescuing vibe-coded systems, scaling architecture to the next maturity, building the team that owns it), ' +
          'provides fractional CTO and architecture leadership, and is the first call for non-technical founders. ' +
          'Contact: discovery@7sigma.io or book a meeting at https://7sigma.io/contact.'
      ),
  },
  {
    name: 'list_services',
    description: 'List the services 7Sigma offers.',
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    execute: async () =>
      text(
        [
          'Fractional CTO: a leader who still builds and operates, taking architecture, hiring, and roadmap decisions.',
          'Architecture and delivery: production systems built in your repos, from foundation to scale.',
          'Team rescue and maturity: picking up where a team left off, stabilizing vibe-coded systems, and building the team that owns them.',
          'First call for non-technical founders: a founding technical advisor before the first hire.',
          `More: ${SITE}/services`,
        ].join('\n')
      ),
  },
  {
    name: 'get_contact_options',
    description: 'Get the ways to contact or engage 7Sigma, with URLs.',
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    execute: async () =>
      text(
        [
          `Contact: ${SITE}/contact`,
          `Advisory enquiries: ${SITE}/contact/advisory`,
          `Partner Network: ${SITE}/contact/partner-network`,
          `Blog: https://blog.7sigma.io`,
        ].join('\n')
      ),
  },
  {
    name: 'navigate_to_page',
    description:
      'Navigate the current browser tab to a 7Sigma page. Allowed pages: home, services, principal, blog, contact, advisory, partner-network.',
    inputSchema: {
      type: 'object',
      properties: {
        page: {
          type: 'string',
          enum: ['home', 'services', 'principal', 'blog', 'contact', 'advisory', 'partner-network'],
        },
      },
      required: ['page'],
      additionalProperties: false,
    },
    execute: async (args) => {
      const map: Record<string, string> = {
        home: '/',
        services: '/services',
        principal: '/principal',
        blog: '/blog',
        contact: '/contact',
        advisory: '/contact/advisory',
        'partner-network': '/contact/partner-network',
      };
      const page = String(args.page || '');
      const path = map[page];
      if (!path) return text(`Unknown page: ${page}`);
      window.location.assign(path);
      return text(`Navigating to ${path}`);
    },
  },
];

export function registerWebMcp(): void {
  try {
    const nav = navigator as unknown as { modelContext?: ModelContext };
    if (nav.modelContext && typeof nav.modelContext.provideContext === 'function') {
      nav.modelContext.provideContext({ tools });
    }
  } catch {
    // Experimental API: never let discovery break the app.
  }
}
